import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { PartnerService } from 'src/app/shared/services/partner.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DataManagementShowModalComponent } from '../../_modals/data-management-show-modal/data-management-show-modal.component';
import { DataManagementCreateShowModalComponent } from '../../_modals/data-management-create-show-modal/data-management-create-show-modal.component';
import { DataManagementCreateSeasonModalComponent } from '../../_modals/data-management-create-season-modal/data-management-create-season-modal.component';
import { DataManagementCreateEpisodeInfoModalComponent } from '../../_modals/data-management-create-episode-info-modal/data-management-create-episode-info-modal.component';
import { AlternateTitlesModalComponent } from '../../_modals/alternate-titles-modal/alternate-titles-modal.component';
import { ImportService } from '../../../services/import.service';

import { MapProToPartnerGroupModalComponent } from '../../_modals/map-pro-to-partner-group-modal/map-pro-to-partner-group-modal.component';
import { MatTableDataSource } from '@angular/material/table';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { UserService } from 'src/app/shared/services/user.service';


interface ProShow {
  id: number,
  title: string,
  description: string,
  partnerGroup_id: number | null,
  alternate_titles: string[],
  seasons: ProShow_Season[],
  // only on a partner group filtered list: true when the show is listed because of an episode
  matched_via_episode?: boolean,
};

interface ProShow_Season {
  id: number,
  show: number,
  season_number: number,
  episodes: ProShow_Episode[],
};

interface ProShow_Episode {
  id: number,
  season: number,
  episode_number: number,
  title: string,
  partnerGroup_id: number | null,
}

@Component({
  selector: 'app-data-management',
  templateUrl: './data-management.component.html',
  styleUrls: ['./data-management.component.scss'],
  animations: [
    trigger('showDetailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', overflow: 'hidden' })
      ),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
    trigger('seasonDetailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', overflow: 'hidden' })
      ),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ]
})

export class DataManagementComponent implements OnInit, OnDestroy {
  expandedShow: Observable<ProShow> = null;
  expandedShowSubject = new BehaviorSubject<ProShow>(null);
  expandedSeason: Observable<ProShow_Season> = null;
  expandedSeasonSubject = new BehaviorSubject<ProShow_Season>(null);

  proShows;
  // dataSource: ProShow[] = [];
  dataSource: MatTableDataSource<ProShow>;
  partnerGroups: any = [];
  mappedShows: any = [];

  searchString = '';
  loadingShows = false;
  showsError = false;
  private _showsRequest: Subscription;
  private _subscriptions: Subscription[] = [];

  // null lists every partner group
  private _selectedPartnerGroupId: number | null = null;
  get selectedPartnerGroupId() {
    return this._selectedPartnerGroupId;
  }
  set selectedPartnerGroupId(id: number | null) {
    if (id === this._selectedPartnerGroupId) {
      return;
    }
    this._selectedPartnerGroupId = id;
    this.loadShows();
  }

  // the group the rows on screen were loaded for, which trails the selection while a load is in flight
  private _shownPartnerGroupId: number | null = null;

  get selectedPartnerGroupName() {
    return this.selectedPartnerGroupId === null ? '' : this.getPartnerGroupNameById(this.selectedPartnerGroupId);
  }

  get shownPartnerGroupName() {
    return this._shownPartnerGroupId === null ? '' : this.getPartnerGroupNameById(this._shownPartnerGroupId);
  }

  get shownCount() {
    return this.dataSource ? this.dataSource.filteredData.length : 0;
  }

  get showEmptyState() {
    return !!this.dataSource && !this.loadingShows && !this.showsError && this.shownCount === 0;
  }

  get emptyStateTitle() {
    if (this.searchString) {
      return 'No matching shows';
    }
    return this.selectedPartnerGroupName ? 'No shows for this partner group' : 'No shows found';
  }

  // dataSource: ProShow[] = [
  //   {
  //     id: 1,
  //     title: 'Test Show 1',
  //     seasons: [
  //       {
  //         id: 101,
  //         title: 'Season 1',
  //         seasonNumber: 1,
  //         episodes: [
  //           {
  //             id: 1001,
  //             title: 'S1 Ep1',
  //             episodeNumber: 1,
  //           },
  //           {
  //             id: 1002,
  //             title: 'S1 Ep2',
  //             episodeNumber: 2,
  //           },
  //         ],
  //       },
  //       {
  //         id: 102,
  //         title: 'Season 2',
  //         seasonNumber: 2,
  //         episodes: [
  //           {
  //             id: 2001,
  //             title: 'S2 Ep1',
  //             episodeNumber: 1,
  //           },
  //           {
  //             id: 2002,
  //             title: 'S2 Ep2',
  //             episodeNumber: 2,
  //           },
  //         ],
  //       },
  //     ],
  //   }
  // ];

  displayedColumns: string[] = ['title', 'seasons', 'notes', 'partnerGroup', 'action'];
  
  constructor(private partnerService: PartnerService, private importService: ImportService, private dialog: MatDialog,
    private snackbarService: SnackBarService, private userService: UserService) {
    this.expandedShow = this.expandedShowSubject.asObservable();
    this.expandedSeason = this.expandedSeasonSubject.asObservable();
   }

  ngOnInit() {
    this.loadShows();

    this._subscriptions.push(
      this.partnerService.getPartnerGroups().subscribe((res: any) => {
        this.partnerGroups = res;
      }),
      // creating a show or assigning one to a group changes the list; reload it with the current filters
      this.partnerService.modalClosed$.subscribe(() => this.loadShows())
    );
  }

  ngOnDestroy() {
    if (this._showsRequest) {
      this._showsRequest.unsubscribe();
    }
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  // Keeps the current rows on screen until the new ones arrive, and drops any request still in flight so a slow
  // response for an earlier partner group can't replace a later one
  loadShows() {
    if (this._showsRequest) {
      this._showsRequest.unsubscribe();
    }
    this.loadingShows = true;
    this.showsError = false;
    const partnerGroupId = this.selectedPartnerGroupId;
    this._showsRequest = this.partnerService.getAllPROShows(partnerGroupId).subscribe(
      (res: ProShow[]) => {
        this._shownPartnerGroupId = partnerGroupId;
        this.proShows = res;
        this.dataSource = new MatTableDataSource<ProShow>(res);
        this.dataSource.filter = this.searchString;
        this.loadingShows = false;
      },
      () => {
        // the old rows belong to the previous filter, so they go rather than sit under the new one
        this.dataSource = new MatTableDataSource<ProShow>([]);
        this.loadingShows = false;
        this.showsError = true;
      }
    );
  }

 
  expandShow(show: ProShow) {
    if (
      this.expandedShowSubject.value &&
      this.expandedShowSubject.value.id == show.id
    ) {
      this.expandedShowSubject.next(null);
      return;
    }
    this.expandedShowSubject.next(show);
  }

  showIsExpanded(show: ProShow) {
    return (
      this.expandedShowSubject.value &&
      this.expandedShowSubject.value.id == show.id
    );
  }

  expandSeason(season: ProShow_Season) {
    if (
      this.expandedSeasonSubject.value &&
      this.expandedSeasonSubject.value.id == season.id
    ) {
      this.expandedSeasonSubject.next(null);
      return;
    }
    this.expandedSeasonSubject.next(season);
  }

  seasonIsExpanded(season: ProShow_Season) {
    return (
      this.expandedSeasonSubject.value &&
      this.expandedSeasonSubject.value.id == season.id
    );
  }

  // the delete endpoints only accept the PRO group, and ErrorInterceptor logs the user out on a 403,
  // so the delete icons are only shown to PRO members
  get canDelete() {
    return this.userService.inGroup('PRO');
  }

  trackById(index: number, item: { id: number }) {
    return item.id;
  }

  // openShowModal(show: ProShow) {
  openShowModal(season: ProShow_Season) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      season: season
    };
    // dialogConfig.data = {
    //   show: show
    // };
    this.dialog.open(DataManagementShowModalComponent, dialogConfig);
  }

  addSeason() {
    console.log('new season add');
  }

  openCreateNewShowModal() {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      partnerGroups: this.partnerGroups
    };
    this.dialog.open(DataManagementCreateShowModalComponent, dialogConfig);
  }

  openCreateNewSeasonModal(show: ProShow) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      show: show
      // showsList: this.dataSource,
    };
    // the modal closes with the saved season (id included) only when one was created
    this.dialog.open(DataManagementCreateSeasonModalComponent, dialogConfig).afterClosed().subscribe((season: ProShow_Season) => {
      if (season) {
        show.seasons.push(season);
      }
    });
  }

  // Add Episode passes no episode and gets a blank form; Edit Episode passes the row, which the modal copies
  openEpisodeInfoModal(season: ProShow_Season, episode?: ProShow_Episode) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      season: season,
      partnerGroups: this.partnerGroups,
      episode: episode
    };
    // the modal closes with the saved row (id included) only when Save Episode succeeded
    this.dialog.open(DataManagementCreateEpisodeInfoModalComponent, dialogConfig).afterClosed().subscribe((savedEpisode: ProShow_Episode) => {
      if (!savedEpisode) {
        return;
      }
      if (episode) {
        Object.assign(episode, savedEpisode);
      } else {
        season.episodes.push(savedEpisode);
      }
    });
  }

  deleteSeason(show: ProShow, season: ProShow_Season) {
    const episodeCount = season.episodes.length;
    const message = episodeCount
      ? `Are you sure you wish to delete Season ${season.season_number}? Its ${episodeCount} episode${episodeCount === 1 ? '' : 's'} will be deleted too.`
      : `Are you sure you wish to delete Season ${season.season_number}?`;
    if (!window.confirm(message)) {
      return;
    }
    this.partnerService.deleteProSeason(season.id).subscribe(
      () => {
        if (this.seasonIsExpanded(season)) {
          this.expandedSeasonSubject.next(null);
        }
        show.seasons = show.seasons.filter(s => s.id !== season.id);
      },
      () => this.snackbarService.showSnackBarMessage('The season could not be deleted.', 'Close', undefined, true)
    );
  }

  deleteEpisode(season: ProShow_Season, episode: ProShow_Episode) {
    if (!window.confirm(`Are you sure you wish to delete Episode ${episode.episode_number}, "${episode.title}"?`)) {
      return;
    }
    this.partnerService.deleteProEpisode(episode.id).subscribe(
      () => {
        season.episodes = season.episodes.filter(e => e.id !== episode.id);
      },
      () => this.snackbarService.showSnackBarMessage('The episode could not be deleted.', 'Close', undefined, true)
    );
  }

  createNewShow() {
    this.partnerService.updateProShow(null, 'New Show 12/2 1', 'Test description. Yippee').subscribe((res: any) => {
      console.log(res);
    });
  }

  createNewSeason() {
    // seasonId, showId, seasonNumber
    this.partnerService.updateProSeason(null, 1, 1).subscribe((res: any) => {
      console.log(res);
    });
  }
  openAlternateTitleModal(show) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      show: show,
      partnerGroups: this.partnerGroups,
      // showsList: this.dataSource,
    };
    dialogConfig.width='600px';
    this.dialog.open(AlternateTitlesModalComponent, dialogConfig);
  }

  openAssignToPartnerGroupModal(show) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      // title: show.showName,
      proShows: this.proShows,
      proShow: show,
      partnerGroups: this.partnerGroups
    };
    this.dialog.open(MapProToPartnerGroupModalComponent, dialogConfig);
  }

  getPartnerGroupNameById(id: number): string {
    const group = this.partnerGroups.find(group => group.id === id);
    return group ? group.name : '';
  }

  // an episode without a group of its own pays its show's group
  episodeHasOwnPartnerGroup(episode: ProShow_Episode) {
    return episode.partnerGroup_id !== null && episode.partnerGroup_id !== undefined;
  }

  episodePartnerGroupId(show: ProShow, episode: ProShow_Episode) {
    return this.episodeHasOwnPartnerGroup(episode) ? episode.partnerGroup_id : show.partnerGroup_id;
  }

  episodePartnerGroupTooltip(show: ProShow, episode: ProShow_Episode) {
    return this.episodeHasOwnPartnerGroup(episode)
      ? this.getPartnerGroupNameById(episode.partnerGroup_id)
      : 'Same as the show: ' + this.getPartnerGroupNameById(show.partnerGroup_id);
  }

  onSearch(text: string) {
    this.searchString = text.trim();
    if (this.dataSource) {
      this.dataSource.filter = this.searchString;
    }
  }

  clearSearch(input: HTMLInputElement) {
    input.value = '';
    this.onSearch('');
    input.focus();
  }
}
